import * as React from 'react'

import { type ConversationBootstrap } from '../hooks/useConversationStream'

export type GlobalSearchContextValue = {
  isOpen: boolean
  conversationBootstrap: Nullable<ConversationBootstrap>
  open: () => void
  close: () => void
  openConversation: (bootstrap: ConversationBootstrap) => void
  endConversation: () => void
}

const DEFAULT_CONTEXT: GlobalSearchContextValue = {
  isOpen: false,
  conversationBootstrap: null,
  open: () => undefined,
  close: () => undefined,
  openConversation: () => undefined,
  endConversation: () => undefined,
}

export const GlobalSearchContext =
  React.createContext<GlobalSearchContextValue>(DEFAULT_CONTEXT)

export const GlobalSearchProvider = ({
  children,
}: React.PropsWithChildren<{}>) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [conversationBootstrap, setConversationBootstrap] =
    React.useState<Nullable<ConversationBootstrap>>(null)

  const open = React.useCallback(() => {
    setIsOpen(true)
  }, [])

  const close = React.useCallback(() => {
    setIsOpen(false)
  }, [])

  const openConversation = React.useCallback(
    (bootstrap: ConversationBootstrap) => {
      setConversationBootstrap(bootstrap)
      setIsOpen(true)
    },
    []
  )

  const endConversation = React.useCallback(() => {
    setConversationBootstrap(null)
  }, [])

  const value = React.useMemo<GlobalSearchContextValue>(
    () => ({
      isOpen,
      conversationBootstrap,
      open,
      close,
      openConversation,
      endConversation,
    }),
    [
      isOpen,
      conversationBootstrap,
      open,
      close,
      openConversation,
      endConversation,
    ]
  )

  return (
    <GlobalSearchContext.Provider value={value}>
      {children}
    </GlobalSearchContext.Provider>
  )
}

export const useGlobalSearch = (): GlobalSearchContextValue => {
  return React.useContext(GlobalSearchContext)
}
